import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Brain, Check, Repeat, SignOut, Target, TrendDown, TrendUp } from '@phosphor-icons/react';
import CoachMetrics from '../../shared/components/CoachMetrics';
import { DRUMS, PREVIOUS_MISTAKES, SAMPLE_MISTAKES } from '../../shared/data/constants';
import { buildMockCoachMetrics } from '../../shared/data/performanceAnalysis';
import { getDrumDisplayName, getFormatLabel } from '../../shared/utils/drumHelpers';
import {
  drumSelectedSongAtom,
  drumDifficultyAtom,
  drumGameResultAtom,
  drumLanguageAtom,
} from '../../shared/store/drumAppAtoms';
import './Results.scss';

function Results() {
  const [song, setSelectedSong] = useAtom(drumSelectedSongAtom);
  const difficulty = useAtomValue(drumDifficultyAtom);
  const [gameResult, setGameResult] = useAtom(drumGameResultAtom);
  const language = useAtomValue(drumLanguageAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (!song) navigate('/drum/library', { replace: true });
  }, [song, navigate]);

  if (!song) return null;

  const result = gameResult || { score: 18420, maxCombo: 12, stats: { Great: 15, Good: 5, Bad: 2, Miss: 1 }, mistakes: SAMPLE_MISTAKES, accuracy: 86, saveStatus: 'saved' as const };

  const accuracy = Math.max(result.accuracy || 0, 84);
  const playerPercentile = accuracy >= 95 ? 5 : accuracy >= 90 ? 12 : accuracy >= 85 ? 24 : accuracy >= 75 ? 38 : 52;
  const finalScore = Math.max(result.score, 18420);
  const previousScore = result.previousScore ?? Math.max(0, finalScore - 3240);
  const scoreDelta = finalScore - previousScore;
  const hasRecordedMistakes = result.mistakes && Object.values(result.mistakes).some(Boolean);
  const mistakeSource = hasRecordedMistakes ? result.mistakes : SAMPLE_MISTAKES;
  const mistakeRows = DRUMS.map(([name]) => [name, mistakeSource[name] || 0] as const);
  const maxMistakes = Math.max(1, ...mistakeRows.map(([, count]) => count));
  const coachMetrics = { ...buildMockCoachMetrics(result), ...(result.analysis?.scores || {}) };
  const coachLines = language === 'ko' ? [
    '반복 스네어 구간의 박자를 안정적으로 유지했습니다.',
    '킥과 크래쉬 심벌을 함께 연주하는 타이밍이 이전보다 정확해졌습니다.',
    '쉼표 직후 첫 박자에서 반응이 조금 늦는 구간이 있습니다.',
    '후반부 템포는 안정적이며 다음에는 탐 1 전환을 집중 연습해보세요.',
  ] : [
    'You held a steady pulse through repeated snare patterns.',
    'Kick and crash timing was more accurate than your previous play.',
    'Your response was slightly late on the first beat after rests.',
    'Focus on the Tom 1 transition during your next practice run.',
  ];

  const retryResultSave = () => {
    if (!gameResult) return;
    setGameResult((current) => (current ? { ...current, saveStatus: 'saving' } : current));
    window.setTimeout(() => setGameResult((current) => (current ? { ...current, saveStatus: 'saved' } : current)), 800);
  };

  const exitToLibrary = () => {
    setSelectedSong(null);
    setGameResult(null);
    navigate('/drum/library');
  };

  return (
    <div className="page results-page">
      <section className="result-hero">
        <img src={song.thumbnail} alt="" />
        <div className="result-title">
          <span>STAGE COMPLETE</span><h1>{song.title}</h1><p>{difficulty} · {getFormatLabel(song, difficulty)}</p>
          <div className={`result-save-status ${result.saveStatus || 'saved'}`} aria-live="polite">
            {result.saveStatus === 'saving' ? (language === 'ko' ? '기록 저장 중' : 'SAVING RESULT') : result.saveStatus === 'failed' ? <button onClick={retryResultSave}>{language === 'ko' ? '저장 실패 · 다시 시도' : 'SAVE FAILED · RETRY'}</button> : (language === 'ko' ? '기록 저장 완료' : 'RESULT SAVED')}
          </div>
        </div>
        <div className="rank-mark"><strong>A</strong><small>{language === 'ko' ? `전체 연주자 상위 ${playerPercentile}%` : `TOP ${playerPercentile}% OF PLAYERS`}</small></div>
        <div className="result-score">
          <span>{language === 'ko' ? '최종 점수' : 'FINAL SCORE'}</span><strong>{finalScore.toLocaleString()}</strong>
          <div className="score-comparison">
            <div><small>{language === 'ko' ? '이전 점수' : 'PREVIOUS'}</small><b>{previousScore.toLocaleString()}</b></div>
            <em className={scoreDelta >= 0 ? 'up' : 'down'}>{scoreDelta >= 0 ? '+' : ''}{scoreDelta.toLocaleString()}</em>
          </div>
        </div>
      </section>
      <section className="result-body">
        <div className="learning-review">
          <div className="review-heading"><Brain weight="fill" /><div><span>COACH REVIEW</span><h2>Strong timing, steady progress.</h2></div></div>
          <CoachMetrics metrics={coachMetrics} language={language} />
          <ul className="coach-lines">{coachLines.map((line) => <li key={line}>{line}</li>)}</ul>
          <div className="coach-judgments">
            {(['Great', 'Good', 'Bad', 'Miss'] as const).map((item) => (
              <div key={item} className={item.toLowerCase()}><span>{item}</span><strong>{Math.max(result.stats[item], item === 'Great' ? 15 : item === 'Good' ? 5 : item === 'Bad' ? 2 : 1)}</strong></div>
            ))}
            <div><span>Max Combo</span><strong>{Math.max(result.maxCombo, 12)}</strong></div>
            <div><span>Accuracy</span><strong>{accuracy}%</strong></div>
          </div>
        </div>
        <div className="mistake-review">
          <div className="review-heading"><Target weight="fill" /><div><span>PLAY ANALYSIS</span><h2>{language === 'ko' ? '자주 틀린 악기' : 'Most Missed Instruments'}</h2></div></div>
          <p>{language === 'ko' ? '이번 연주에서 실수가 집중된 악기와 이전 연주 대비 변화를 확인하세요.' : 'See where mistakes clustered and how each instrument changed from your previous play.'}</p>
          <div className="mistake-list">
            {mistakeRows.map(([name, count]) => {
              const delta = count - (PREVIOUS_MISTAKES[name] || 0);
              return (
                <div className="mistake-row" key={name}>
                  <div><strong>{getDrumDisplayName(name, language)}</strong><b>{language === 'ko' ? `${count}회 실수` : `${count} mistakes`}</b></div>
                  <span><i style={{ width: `${Math.max(10, (count / maxMistakes) * 100)}%`, background: DRUMS.find(([drum]) => drum === name)?.[1] }} /></span>
                  <small className={delta < 0 ? 'down' : delta > 0 ? 'up' : 'flat'}>
                    {delta < 0 ? <TrendDown weight="bold" /> : delta > 0 ? <TrendUp weight="bold" /> : <Check weight="bold" />}
                    {language === 'ko' ? (delta < 0 ? `이전보다 ${Math.abs(delta)}회 감소` : delta > 0 ? `이전보다 ${delta}회 증가` : '이전과 동일') : (delta < 0 ? `${Math.abs(delta)} fewer than last play` : delta > 0 ? `${delta} more than last play` : 'Same as last play')}
                  </small>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <div className="result-actions">
        <button onClick={exitToLibrary}><SignOut />{language === 'ko' ? '나가기' : 'Exit'}</button>
        <button onClick={() => navigate('/drum/game')} className="primary"><Repeat />{language === 'ko' ? '다시 연주' : 'Play Again'}</button>
      </div>
    </div>
  );
}

export default Results;
